import {useEffect} from 'react';
import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {loadStripe} from '../stripe';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {startTransaction, updateTransaction, z} from '@peachpay/checkout-blocks';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {parsePaymentData} from '../stripe';
import {getErrorMessage} from '../../../../../../frontend/@shared/ts/error';

const SettingsSchema = z.object({
	connect_id: z.string(),
	currency_fallback_required: z.string().nullable(),
	description: z.string(),
	icon_url: z.string(),
	public_key: z.string(),
	setup_intent_nonce: z.string(),
	setup_intent_url: z.string(),
	supports: z.string().array(),
	title: z.string(),
});

const parsedData = SettingsSchema.safeParse(
	getSetting('peachpay_stripe_afterpay_data', false),
);

if (!parsedData.success) {
	throw new Error(
		`PeachPay Stripe Afterpay data is not available \n\n${parsedData.error.message}`,
	);
}

const method_data = parsedData.data;

const title = decodeEntities(method_data.title);
const description = decodeEntities(method_data.description);

loadStripe(method_data.public_key, method_data.connect_id)
	.then(stripe => {
		if (stripe === null) {
			throw new Error(__('Stripe is null', 'peachpay-for-woocommerce'));
		}

		if (typeof stripe === 'string') {
			throw new Error(stripe);
		}

		/**
     * Content component
     */
		const Content = (context: PaymentMethodInterface) => {
			const {emitResponse, eventRegistration, billing} = context;
			const {onPaymentSetup, onCheckoutSuccess, onCheckoutFail} = eventRegistration;

			useEffect(() => {
				const unsubscribe = onPaymentSetup(async () => {
					try {
						const start_transaction = await startTransaction('peachpay_stripe_afterpay');

						if (start_transaction.error) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: getErrorMessage(start_transaction.error),
							};
						}

						if (start_transaction.result === undefined) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: __('Missing transaction ID', 'peachpay-for-woocommerce'),
							};
						}

						const transaction_ID = start_transaction.result;

						const tokenResult = await stripe.createPaymentMethod({
							type: 'afterpay_clearpay',
							billing_details: {
								address: {
									city: billing.billingAddress.city,
									country: billing.billingAddress.country,
									line1: billing.billingAddress.address_1,
									line2: billing.billingAddress.address_2,
									postal_code: billing.billingAddress.postcode,
									state: billing.billingAddress.state,
								},
								email: billing.billingAddress.email,
								name: billing.billingAddress.first_name + ' ' + billing.billingAddress.last_name,
								phone: billing.billingAddress.phone,
							},
						});

						if (tokenResult.error) {
							const message
                = tokenResult.error.message
                ?? __('Failed to tokenize payment method.', 'peachpay-for-woocommerce');

							await updateTransaction(transaction_ID, {note: message});
							return {
								type: emitResponse.responseTypes.ERROR,
								message,
							};
						}

						return {
							type: emitResponse.responseTypes.SUCCESS,
							meta: {
								paymentMethodData: {
									peachpay_transaction_id: transaction_ID,
									peachpay_stripe_payment_method_id: tokenResult.paymentMethod.id,
								},
							},
						};
					} catch (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: getErrorMessage(error),
						};
					}
				});

				return unsubscribe;
			}, [billing.billingAddress, onPaymentSetup]);

			useEffect(() => {
				const unsubscribe = onCheckoutSuccess(async (data: {redirectUrl: string}) => {
					const dataURL = new URL(data.redirectUrl);

					const [name, encodedStringData] = dataURL.hash.split('=');

					if (dataURL.hash === '' || name !== '#payment_data' || !encodedStringData) {
						return;
					}

					const paymentData = parsePaymentData(encodedStringData);

					if (paymentData === null) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __('Failed to parse payment data', 'peachpay-for-woocommerce'),
						};
					}

					const transaction_ID = paymentData.transaction_id;
					const clientSecret = paymentData.data.client_secret;
					const intermediateURL = paymentData.intermediate_url;
					const successURL = paymentData.success_url;

					const {paymentIntent, error} = await stripe.confirmAfterpayClearpayPayment(clientSecret, {
						return_url: intermediateURL,
					});
					if (error) {
						const message = error?.message ?? __('Failed to confirm payment.', 'peachpay-for-woocommerce');

						await updateTransaction(transaction_ID, {note: message});
						return {
							type: emitResponse.responseTypes.ERROR,
							message,
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					if (paymentIntent) {
						return {
							type: emitResponse.responseTypes.SUCCESS,
							redirectUrl: successURL,
						};
					}

					const message = __('Payment intent missing', 'peachpay-for-woocommerce');
					await updateTransaction(transaction_ID, {note: message});
					return {
						type: emitResponse.responseTypes.ERROR,
						message,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutSuccess]);

			useEffect(() => {
				const unsubscribe = onCheckoutFail((data: CheckoutAfterProcessingWithErrorEventData) => {
					if (data.processingResponse?.paymentStatus === emitResponse.responseTypes.FAIL) {
						return {
							type: emitResponse.responseTypes.FAIL,
							message: data.processingResponse.paymentDetails['message'],
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					return {
						type: emitResponse.responseTypes.FAIL,
						message:
              data.processingResponse?.message
              ?? __('Checkout failed unexpectedly', 'peachpay-for-woocommerce')
                + `${JSON.stringify(data)}`,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutFail]);

			if (method_data.currency_fallback_required) {
				return <CurrencyFallback name={title} currency={method_data.currency_fallback_required} />;
			}

			return (
				<>
					<img style={{maxHeight: '25px'}} src={method_data.icon_url} />
					<p style={{textAlign: 'left', margin: '0.5rem 0 0'}}>{description}</p>
				</>
			);
		};

		/**
     * Label component
     *
     * @param {PaymentMethodInterface} props Props from payment API.
     */
		const Label = (props: PaymentMethodInterface) => {
			const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
			return (
				<>
					<PaymentMethodLabel text={title} />
					<PaymentMethodIcons
						className='pp-label-icon'
						icons={[
							{
								id: 'peachpay_stripe_afterpay',
								src: method_data.icon_url,
								alt: title,
							},
						]}
					/>
				</>
			);
		};

		registerPaymentMethod({
			ariaLabel: title,
			canMakePayment(data) {
				return data.paymentMethods.includes('peachpay_stripe_afterpay');
			},
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			content: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			edit: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			label: <Label />,
			name: 'peachpay_stripe_afterpay',
			supports: {
				features: method_data.supports,
				showSavedCards: false,
				showSaveOption: false,
			},
		});
	})
	.catch(error => {
		throw new Error(getErrorMessage(error));
	});
